import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageSquare } from 'lucide-react';
import { Textarea } from './ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const kg = (w) => (w == null ? '' : String(w).replace('.', ','));

export const formatSets = (ex) => {
  if (ex.sets?.length) {
    const one = (s) => `${s.weight != null ? `${kg(s.weight)}×` : ''}${s.reps || '?'}`;
    const warm = ex.sets.filter((s) => s.warmup);
    const work = ex.sets.filter((s) => !s.warmup).map(one).join(' · ');
    return warm.length ? `rozgrz. ${warm.map(one).join(' · ')} | ${work || '—'}` : work;
  }
  if (ex.actual_sets || ex.actual_reps || ex.actual_weight) {
    return `${ex.actual_sets ?? '?'}× ${ex.actual_reps ?? '?'}${ex.actual_weight ? ` @ ${kg(ex.actual_weight)} kg` : ''}`;
  }
  return null;
};

const SOURCE = { journal: 'dziennik', player: 'odtwarzacz' };

// One logged workout as the trainer sees it: every set, the client's notes,
// and a form for the trainer's feedback (per exercise + overall).
const LogItem = ({ log, onUpdated, defaultOpen }) => {
  const [editing, setEditing] = useState(false);
  const [overall, setOverall] = useState(log.trainer_comment || '');
  const [perEx, setPerEx] = useState(() => log.exercises.map((e) => e.trainer_comment || ''));
  const [saving, setSaving] = useState(false);
  const hasClientNotes = log.overall_notes || log.exercises.some((e) => e.notes);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/api/workout-logs/${log.id}/comments`, {
        comment: overall, exercises: perEx.map((comment, index) => ({ index, comment })),
      }, { withCredentials: true });
      onUpdated(res.data);
      setEditing(false);
      toast.success('Komentarz zapisany — klient zobaczy go w swojej historii');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Nie udało się zapisać komentarza');
    } finally {
      setSaving(false);
    }
  };

  return (
    <details open={defaultOpen} className="glass-soft rounded-2xl px-4 py-3" data-testid={`workout-log-${log.id}`}>
      <summary className="flex items-center justify-between gap-3 cursor-pointer list-none">
        <span className="font-semibold text-sm">
          {new Date(log.log_date).toLocaleDateString('pl-PL')} · Dzień {log.day_number}
          {SOURCE[log.source] && <span className="font-normal text-[var(--text-muted)]"> · {SOURCE[log.source]}</span>}
        </span>
        <span className="flex flex-wrap justify-end items-center gap-1.5 min-w-0">
          {hasClientNotes && <span className="chip text-[11px]">uwagi</span>}
          {log.trainer_comment || log.exercises.some((e) => e.trainer_comment)
            ? <span className="chip text-[11px] border-[#C9A84C]/50">skomentowany</span>
            : <span className="chip text-[11px]">do komentarza</span>}
          {log.duration_minutes && <span className="chip text-[11px]">{log.duration_minutes} min</span>}
        </span>
      </summary>
      <div className="mt-3 flex flex-col gap-2.5">
        {log.exercises.map((ex, i) => (
          <div key={i} className="flex flex-col gap-1">
            <p className="text-sm">
              <span className={ex.completed ? '' : 'text-[var(--text-muted)]'}>{ex.completed ? '✓' : '—'} {ex.exercise_name}</span>
              {formatSets(ex) && <span className="text-xs text-[var(--text-muted)]"> — {formatSets(ex)}</span>}
            </p>
            {ex.notes && <p className="text-xs text-[var(--text-muted)] pl-4">Klient: „{ex.notes}”</p>}
            {!editing && ex.trainer_comment && <p className="text-xs text-[#C9A84C] pl-4">Ty: „{ex.trainer_comment}”</p>}
            {editing && (
              <Textarea value={perEx[i]} rows={1} placeholder="Komentarz do ćwiczenia (opcjonalnie)"
                onChange={(e) => setPerEx((all) => all.map((c, j) => (j === i ? e.target.value : c)))}
                className="ml-4 w-[calc(100%-1rem)] bg-[var(--bg-primary)] border-[var(--border-soft)] rounded-xl text-xs min-h-0" />
            )}
          </div>
        ))}
        {log.overall_notes && <p className="text-sm italic">Klient: „{log.overall_notes}”</p>}
        {!editing && log.trainer_comment && <p className="text-sm text-[#C9A84C]">Ty: „{log.trainer_comment}”</p>}
        {editing ? (
          <div className="flex flex-col gap-2 mt-1">
            <Textarea value={overall} onChange={(e) => setOverall(e.target.value)} rows={2}
              placeholder="Komentarz do całego treningu — klient zobaczy go w historii"
              className="bg-[var(--bg-primary)] border-[var(--border-soft)] rounded-xl text-sm" data-testid="log-comment-input" />
            <div className="flex gap-2">
              <button type="button" onClick={save} disabled={saving} className="cta-primary h-9 px-4 rounded-full text-xs font-semibold" data-testid="log-comment-save">
                {saving ? 'Zapisywanie…' : 'Zapisz komentarz'}
              </button>
              <button type="button" onClick={() => setEditing(false)} className="cta-ghost h-9 px-4 rounded-full text-xs font-semibold">Anuluj</button>
            </div>
          </div>
        ) : (
          <button type="button" onClick={() => setEditing(true)} className="cta-ghost h-9 px-3.5 rounded-full text-xs font-semibold flex items-center gap-1.5 self-start mt-1"
            data-testid="log-comment-open">
            <MessageSquare className="w-3.5 h-3.5" /> {log.trainer_comment || log.exercises.some((e) => e.trainer_comment) ? 'Edytuj komentarz' : 'Skomentuj'}
          </button>
        )}
      </div>
    </details>
  );
};

export const WorkoutLogFeed = ({ logs, onUpdated }) => (
  logs.length === 0 ? (
    <p className="text-sm text-[var(--text-muted)]">Klient nie zalogował jeszcze żadnego treningu.</p>
  ) : (
    <div className="flex flex-col gap-2">
      {logs.slice(0, 10).map((log, i) => <LogItem key={log.id} log={log} onUpdated={onUpdated} defaultOpen={i === 0} />)}
    </div>
  )
);
